/**
 * /affiliation/:token — where an agency representative answers "is this
 * person with you?" (M43). Public: the representative has no Attest account,
 * and the link is the only credential. Opening the page changes nothing —
 * mail scanners open links — only a tap on a button records an answer.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { baseUrl } from '../lib/api.js';

const worker = (w) => (w === 'paid' ? 'Paid' : w === 'volunteer' ? 'Volunteer' : w || null);

async function call(path, options = {}) {
  let res;
  try {
    res = await fetch(`${baseUrl}${path}`, {
      ...options,
      headers: options.body ? { 'Content-Type': 'application/json' } : undefined,
    });
  } catch {
    throw new Error('No connection. Check your signal and try again.');
  }
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new Error(body?.error?.message ?? `Something went wrong (${res.status}).`);
  return body?.data ?? body;
}

export function AffiliationAnswerPage() {
  const { token } = useParams();
  const [view, setView] = useState(undefined);
  const [error, setError] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    call(`/api/v1/affiliation-answer/${encodeURIComponent(token)}`)
      .then(setView)
      .catch((e) => {
        setError(e);
        setView(null);
      });
  }, [token]);

  async function answer(value) {
    setBusy(true);
    setError(null);
    try {
      setView(
        await call(`/api/v1/affiliation-answer/${encodeURIComponent(token)}`, {
          method: 'POST',
          body: JSON.stringify({ answer: value, note: note.trim() || null }),
        })
      );
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  const as = view ? [worker(view.worker_type), view.role_title].filter(Boolean).join(', ') : '';

  return (
    <main className="checkin">
      <div className="card">
        {view === undefined && <p className="muted">Loading…</p>}
        {view === null && (
          <>
            <h1>Link not recognised</h1>
            <p>{error?.message}</p>
          </>
        )}
        {view && (
          <>
            <h1>Is this right?</h1>
            <p>
              {view.asked_by ?? 'The registration office'}
              {view.college ? ` at ${view.college}` : ''} is checking a continuing-education registration.
            </p>
            <p style={{ padding: '12px 14px', background: 'var(--surface-quiet)', borderRadius: 6 }}>
              <strong>{view.person_name}</strong> attested{view.attested_on ? ` on ${view.attested_on}` : ''} that they are affiliated
              with <strong>{view.agency_name}</strong>
              {as ? ` as: ${as}` : ''}.
            </p>
            {view.note && <p className="muted">Note from the office: {view.note}</p>}

            {view.status === 'confirmed' && <p className="banner banner--success">✓ You confirmed this. Thank you — nothing more is needed.</p>}
            {view.status === 'denied' && <p className="banner banner--success">You said this is not right. Thank you — the office has been told.</p>}
            {view.status === 'pending' && view.expired && (
              <p className="banner banner--error">This link has expired. Reply to the email instead, or ask the office to send a new one.</p>
            )}
            {view.status === 'pending' && !view.expired && (
              <>
                {error && (
                  <p className="banner banner--error" role="alert">
                    {error.message}
                  </p>
                )}
                <label className="field">
                  <span>Anything to add? (optional)</span>
                  <input type="text" value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} disabled={busy} />
                </label>
                <p className="toolbar">
                  <button type="button" className="btn btn--primary" disabled={busy} onClick={() => answer('confirmed')}>
                    Yes, that is right
                  </button>
                  <button type="button" className="btn" disabled={busy} onClick={() => answer('denied')}>
                    No, that is not right
                  </button>
                </p>
                <p className="muted">One tap answers. Your answer goes to the office that asked and is kept with the person&apos;s record.</p>
              </>
            )}
          </>
        )}
      </div>
    </main>
  );
}

export default AffiliationAnswerPage;
